import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Target, Plus, TrendingUp } from "lucide-react";
import { useAuth } from "@/lib/auth";

export default function Performance() {
  const { user } = useAuth();
  const [goals, setGoals] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", period: `Q${Math.ceil((new Date().getMonth()+1)/3)}-${new Date().getFullYear()}`, target_metric: "", target_value: 100, weight: 1.0 });
  const [updating, setUpdating] = useState(null);
  const [progress, setProgress] = useState({ current_value: 0, notes: "" });

  const load = async () => {
    const [g, r] = await Promise.all([api.get("/goals", { params: { scope: "mine" } }), api.get("/reviews")]);
    setGoals(g.data); setReviews(r.data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try { await api.post("/goals", { ...form, target_value: Number(form.target_value), weight: Number(form.weight) });
      toast.success("Goal created"); setOpen(false);
      setForm({ title: "", description: "", period: form.period, target_metric: "", target_value: 100, weight: 1.0 });
      load();
    } catch (e) { toast.error("Failed"); }
  };
  const update = async () => {
    try { await api.patch(`/goals/${updating.id}/progress`, { current_value: Number(progress.current_value), notes: progress.notes });
      toast.success("Progress updated"); setUpdating(null); setProgress({ current_value: 0, notes: "" }); load();
    } catch { toast.error("Failed"); }
  };

  const avgProgress = goals.length ? Math.round(goals.reduce((a, g) => a + (g.progress || 0), 0) / goals.length) : 0;
  const atRisk = goals.filter(g => g.status === "at_risk").length;
  const achieved = goals.filter(g => g.status === "achieved").length;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid="performance-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Services</div>
          <h1 className="text-3xl font-[Outfit] font-semibold tracking-tight text-slate-900">Performance</h1>
          <p className="text-sm text-slate-600 mt-1">Goals, KPIs, and review history</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-slate-900 hover:bg-slate-800" data-testid="new-goal-btn"><Plus className="w-4 h-4 mr-1"/>New goal</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create goal</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="mt-1" data-testid="goal-title"/></div>
              <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="mt-1" rows={2}/></div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Period</Label><Input value={form.period} onChange={(e) => setForm({...form, period: e.target.value})} className="mt-1"/></div>
                <div><Label>Target metric</Label><Input value={form.target_metric} onChange={(e) => setForm({...form, target_metric: e.target.value})} className="mt-1" placeholder="e.g. features shipped"/></div>
                <div><Label>Target value</Label><Input type="number" value={form.target_value} onChange={(e) => setForm({...form, target_value: e.target.value})} className="mt-1"/></div>
              </div>
            </div>
            <DialogFooter><Button onClick={create} disabled={!form.title} className="bg-slate-900" data-testid="goal-submit">Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="p-4 border-slate-200 shadow-none"><div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Total goals</div><div className="mt-2 text-2xl font-[Outfit] font-semibold">{goals.length}</div></Card>
        <Card className="p-4 border-cyan-200 bg-cyan-50/40 shadow-none"><div className="text-xs uppercase tracking-wider text-cyan-700 font-semibold">Avg progress</div><div className="mt-2 text-2xl font-[Outfit] font-semibold text-cyan-900">{avgProgress}%</div></Card>
        <Card className="p-4 border-emerald-200 bg-emerald-50/40 shadow-none"><div className="text-xs uppercase tracking-wider text-emerald-700 font-semibold">Achieved</div><div className="mt-2 text-2xl font-[Outfit] font-semibold text-emerald-900">{achieved}</div></Card>
        <Card className="p-4 border-red-200 bg-red-50/40 shadow-none"><div className="text-xs uppercase tracking-wider text-red-700 font-semibold">At risk</div><div className="mt-2 text-2xl font-[Outfit] font-semibold text-red-900">{atRisk}</div></Card>
      </div>

      <Tabs defaultValue="goals">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="goals">Goals</TabsTrigger>
          <TabsTrigger value="reviews">Reviews</TabsTrigger>
        </TabsList>
        <TabsContent value="goals" className="mt-4 space-y-2">
          {goals.length === 0 ? (
            <Card className="p-10 text-center border-slate-200 shadow-none"><Target className="w-8 h-8 mx-auto text-slate-300 mb-2"/><div className="text-sm text-slate-500">No goals yet.</div></Card>
          ) : goals.map(g => (
            <Card key={g.id} className="p-4 border-slate-200 shadow-none" data-testid={`goal-${g.id}`}>
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-md bg-cyan-500/10"><Target className="w-4 h-4 text-cyan-600"/></div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-sm font-medium text-slate-900">{g.title}</div>
                    <Badge variant="outline" className="text-[10px]">{g.period}</Badge>
                    <Badge className={`${g.status === "achieved" ? "bg-emerald-100 text-emerald-800" : g.status === "at_risk" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"} border-0 capitalize`}>{g.status?.replace("_", " ")}</Badge>
                  </div>
                  {g.description && <div className="text-xs text-slate-500 mt-1">{g.description}</div>}
                  <div className="mt-3">
                    <div className="flex justify-between text-xs mb-1"><span className="text-slate-500">{g.current_value} / {g.target_value} {g.target_metric}</span><span className="font-semibold">{g.progress}%</span></div>
                    <Progress value={g.progress} className="h-2"/>
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => { setUpdating(g); setProgress({ current_value: g.current_value || 0, notes: "" }); }} data-testid={`goal-update-${g.id}`}><TrendingUp className="w-3.5 h-3.5 mr-1"/>Update</Button>
              </div>
            </Card>
          ))}
        </TabsContent>
        <TabsContent value="reviews" className="mt-4 space-y-2">
          {reviews.length === 0 ? (
            <Card className="p-10 text-center border-slate-200 shadow-none"><div className="text-sm text-slate-500">No reviews yet.</div></Card>
          ) : reviews.map(r => (
            <Card key={r.id} className="p-4 border-slate-200 shadow-none">
              <div className="flex items-center gap-3">
                <Badge variant="outline">{r.period}</Badge>
                <Badge className={`${r.status === "final" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"} border-0 capitalize`}>{r.status}</Badge>
                {r.rating && <Badge className="bg-cyan-100 text-cyan-800 border-0">{r.rating} / 5</Badge>}
              </div>
              {r.summary && <div className="text-sm text-slate-700 mt-2">{r.summary}</div>}
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      <Dialog open={!!updating} onOpenChange={(v) => !v && setUpdating(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Update goal progress</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Current value</Label><Input type="number" value={progress.current_value} onChange={(e) => setProgress({...progress, current_value: e.target.value})} className="mt-1"/></div>
            <div><Label>Notes</Label><Textarea value={progress.notes} onChange={(e) => setProgress({...progress, notes: e.target.value})} className="mt-1" rows={3}/></div>
          </div>
          <DialogFooter><Button onClick={update} className="bg-slate-900">Save progress</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
